import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { VerifyDemo } from '../components/VerifyDemo';
import { localizedBlogHref, localizedDocsHref } from '../i18n/localizedUrl';
import { useAuth } from '../hooks/useAuth';
import { prefetchGalleryThumbs } from '../lib/templateThumbs';

const CheckIcon = () => (
  <svg className="h-5 w-5 text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = () => (
  <svg className="h-5 w-5 text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export function LandingPage() {
  const { t, i18n } = useTranslation('landing');
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Warm the /badge-templates gallery images behind the scenes so the gallery
  // paints instantly when the visitor clicks "Browse all templates". Kicks off
  // only after the landing page itself has loaded, at idle, and never on
  // metered/very-slow connections (see prefetchGalleryThumbs). Aborts on leave.
  useEffect(() => {
    let cancel: (() => void) | undefined;
    const start = () => {
      cancel = prefetchGalleryThumbs();
    };
    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
    return () => {
      window.removeEventListener('load', start);
      cancel?.();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a1a] text-white">
      {/* ── NAVBAR ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#0a0a1a]/80 backdrop-blur-xl border-b border-white/5' : ''
        }`}
      >
        <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/">
            <img src="/assets/logo/logo-full-white-text.png" alt="badges.ninja" className="h-8" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/badge-templates" className="hidden text-sm text-gray-400 hover:text-white transition sm:block">
              {t('nav.templates', 'Templates')}
            </Link>
            <a href="#pricing" className="hidden text-sm text-gray-400 hover:text-white transition sm:block">
              {t('nav.pricing', 'Pricing')}
            </a>
            <a href={localizedDocsHref('/docs/guide/auth', i18n.language)} className="hidden text-sm text-gray-400 hover:text-white transition sm:block">
              {t('nav.docs', 'Docs')}
            </a>
            <a href={localizedBlogHref('/blog/', i18n.language)} className="hidden text-sm text-gray-400 hover:text-white transition sm:block">
              {t('nav.blog', 'Blog')}
            </a>
            <LanguageSwitcher variant="dark" />
            {authLoading ? null : isAuthenticated ? (
              <Link
                to="/dashboard"
                className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium hover:bg-purple-500 transition"
              >
                {t('nav.goToDashboard', 'Go to Dashboard')}
              </Link>
            ) : (
              <>
                <Link to="/signin" className="hidden sm:inline-block text-sm text-gray-300 hover:text-white transition">
                  {t('nav.signIn', 'Sign In')}
                </Link>
                <Link
                  to="/signup"
                  className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium hover:bg-purple-500 transition"
                >
                  {t('nav.signUp', 'Get Started')}
                </Link>
              </>
            )}
          </div>
        </nav>
      </header>

      {/* ── HERO (2-column) ── */}
      <section className="relative overflow-hidden pt-32 pb-20">
        {/* Animated gradient blobs */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-purple-600/30 blur-[128px] animate-pulse" />
          <div className="absolute top-1/3 right-1/4 h-80 w-80 rounded-full bg-indigo-500/25 blur-[100px] animate-pulse [animation-delay:1s]" />
          <div className="absolute bottom-1/4 left-1/2 h-72 w-72 rounded-full bg-violet-500/20 blur-[120px] animate-pulse [animation-delay:2s]" />
        </div>

        <div className="mx-auto max-w-6xl px-6 flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left column (60%) */}
          <div className="flex-[3] text-center lg:text-left">
            {/* Compliance badge */}
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-purple-500/30 bg-purple-500/10 px-4 py-1.5 text-sm text-purple-300">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-purple-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-purple-500" />
              </span>
              {t('hero.badge', 'Open Badges 2.0 & 3.0 · W3C Verifiable Credentials')}
            </div>

            <h1 className="text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl" style={{ lineHeight: 1.08 }}>
              {t('hero.title', 'Issue Verifiable Open Badges in Minutes — Free')}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-400">
              {t(
                'hero.subtitle2',
                'Design and issue Open Badges 2.0 & 3.0 credentials with a visual editor and permanent, verifiable hosting — export any credential as a W3C Verifiable Credential for digital wallets. Free tier with every feature, no sales call, no setup fee. Your first credential in 5 minutes.'
              )}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
              <Link
                to="/designer"
                className="rounded-lg bg-purple-600 px-8 py-3 text-base font-semibold hover:bg-purple-500 transition shadow-lg shadow-purple-600/25"
              >
                {t('hero.ctaDesign', 'Design your badge — free')}
              </Link>
              <Link
                to="/signup"
                className="rounded-lg border border-white/10 px-8 py-3 text-base font-medium text-gray-300 hover:bg-white/5 transition"
              >
                {t('hero.ctaSignup', 'Sign up free')}
              </Link>
            </div>
            <p className="mt-4 text-center text-sm text-gray-500 lg:text-left">
              {t('hero.ctaNote2', 'No account needed to start · No credit card · Open Badges 2.0 & 3.0')}
            </p>
          </div>

          {/* Right column (40%) — Badge preview card */}
          <div className="flex-[2] w-full max-w-sm">
            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-5 transform rotate-2 hover:rotate-0 transition-transform duration-500 shadow-2xl shadow-purple-900/20">
              <img
                src="/assets/landing/developer-associate.svg"
                alt="Developer Associate badge"
                width={384}
                height={384}
                fetchPriority="high"
                decoding="async"
                className="w-full rounded-xl border border-white/5 object-contain aspect-square bg-white/5"
              />
              <div className="mt-4 space-y-1.5">
                <h3 className="text-lg font-semibold">Developer Associate</h3>
                <p className="text-sm text-gray-400">Issued by Acme Corp</p>
                <div className="flex items-center gap-4 pt-1">
                  <span className="inline-flex items-center gap-1.5 text-sm text-green-400">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Verified
                  </span>
                  <span className="text-xs text-gray-500">Open Badges 2.0 & 3.0</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR — scale-independent capability signals (no volume vanity metrics) ── */}
      <section className="border-y border-white/5 bg-white/[0.02]">
        <div className="mx-auto max-w-4xl px-6 py-8">
          <div className="grid grid-cols-1 divide-y divide-white/5 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            {[
              { value: '5 min', label: t('trust.time', 'To your first credential') },
              { value: '$0', label: t('trust.free', 'Free tier — every feature included') },
              { value: '1-click', label: t('trust.share', 'Add to LinkedIn & digital wallets') },
            ].map((s) => (
              <div key={s.label} className="px-6 py-4 text-center sm:py-2">
                <p className="text-3xl font-bold text-purple-400">{s.value}</p>
                <p className="mt-1 text-sm text-gray-400">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-gray-500">
            {t('trust.standards', 'Open Badges 2.0 & 3.0 (1EdTech / W3C) · Independently verifiable · Permanently hosted')}
          </p>
        </div>
      </section>

      {/* ── PRODUCT SHOWCASE — the real editor ── */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-purple-400">
            {t('showcase.label', 'See it in action')}
          </p>
          <h2 className="mt-2 text-3xl font-bold sm:text-4xl">
            {t('showcase.heading', 'Drag, drop, and issue your first Open Badge in under 5 minutes')}
          </h2>
          <p className="mt-4 text-gray-400">
            {t('showcase.subtitle', 'A real visual editor — templates, fonts, shapes, QR codes, and dynamic recipient fields. No Photoshop, no signup to try.')}
          </p>
        </div>
        <div className="relative mt-10">
          <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-purple-600/10 blur-2xl" />
          <Link to="/designer" className="block">
            <img
              src="/assets/landing/designer-hero.png"
              alt={t('showcase.alt', 'The badges.ninja visual badge editor')}
              className="mx-auto w-full max-w-5xl rounded-2xl border border-white/10 shadow-2xl shadow-purple-900/30 transition hover:border-purple-500/30"
              loading="lazy"
            />
          </Link>
        </div>
        <div className="mt-8 text-center">
          <Link to="/designer" className="rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-semibold hover:bg-purple-500 transition">
            {t('showcase.cta', 'Open the editor — free →')}
          </Link>
        </div>
      </section>

      {/* ── CERTIFICATE SHOWCASE — the linked PDF outcome ── */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
          {/* Copy */}
          <div className="flex-1 text-center lg:text-left">
            <p className="text-sm font-semibold uppercase tracking-widest text-purple-400">
              {t('certShowcase.label', "The recipient's takeaway")}
            </p>
            <h2 className="mt-2 text-3xl font-bold sm:text-4xl">
              {t('certShowcase.heading', 'Every badge comes with a matching certificate')}
            </h2>
            <p className="mt-4 text-gray-400">
              {t('certShowcase.subtitle', 'Issue a verifiable Open Badge and hand recipients a beautiful, print-ready PDF certificate — designed in the same editor and backed by the same public verification page. One award, two credentials they can share and print.')}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-gray-300">
              {[
                t('certShowcase.point1', 'Print-ready PDF, auto-personalized per recipient'),
                t('certShowcase.point2', 'Same verification page as the badge — scan the QR to verify'),
                t('certShowcase.point3', 'Designed in the same visual editor'),
              ].map((p) => (
                <li key={p} className="flex items-start gap-2 justify-center lg:justify-start">
                  <CheckIcon />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <a href={localizedDocsHref('/docs/guide/certificates', i18n.language)} className="text-sm font-medium text-purple-400 hover:text-purple-300 transition">
                {t('certShowcase.cta', 'See how certificates work →')}
              </a>
            </div>
          </div>
          {/* Certificate visual */}
          <div className="w-full flex-1">
            <div className="relative">
              <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-purple-600/10 blur-2xl" />
              <img
                src="/assets/landing/certificate-hero.png"
                alt={t('certShowcase.alt', 'A print-ready PDF certificate linked to a verifiable badge')}
                className="w-full rounded-xl border border-white/10 shadow-2xl shadow-purple-900/30"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF LOGO BAR ── */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <h2 className="text-center text-2xl font-bold sm:text-3xl">
          {t('social.trustedHeading', 'Trusted by teams around the world')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-400 mb-10">
          {t('social.trustedSub', 'Educators, training providers, and organizations issue with badges.ninja.')}
        </p>
        <div className="relative overflow-hidden">
          <div className="flex animate-[scroll_25s_linear_infinite] gap-x-12 whitespace-nowrap">
            {[
              'Texas A&M University', 'UNINETTUNO', 'EDGE Education',
              'Strategify', 'upLVL Inc.', 'National Language Service Corp',
              'PMOfficers', 'Statemind', 'aicerts', 'Keytia Academy',
              'Enea Digital Business School', 'Projectum Cert',
              // Duplicate for seamless loop
              'Texas A&M University', 'UNINETTUNO', 'EDGE Education',
              'Strategify', 'upLVL Inc.', 'National Language Service Corp',
              'PMOfficers', 'Statemind', 'aicerts', 'Keytia Academy',
              'Enea Digital Business School', 'Projectum Cert',
            ].map((name, i) => (
              <span key={`${name}-${i}`} className="text-base font-semibold text-gray-400/60 flex-shrink-0">
                {name}
              </span>
            ))}
          </div>
          {/* Fade edges */}
          <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#0a0a1a] to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#0a0a1a] to-transparent pointer-events-none" />
        </div>
        <style>{`
          @keyframes scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </section>

      {/* ── FEATURES BENTO GRID ── */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <p className="text-sm font-semibold uppercase tracking-widest text-purple-400">
          {t('features.label', 'Features')}
        </p>
        <h2 className="mt-2 text-3xl font-bold sm:text-4xl">
          {t('features.heading', 'Everything you need to issue badges')}
        </h2>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Visual Badge Designer — span 2 */}
          <div className="group rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-1 hover:border-purple-500/30 sm:col-span-2">
            <div className="mb-4 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold">{t('features.designer.title', 'Visual Badge Designer')}</h3>
            <p className="mt-2 text-sm text-gray-400">
              {t('features.designer.desc', 'A three-column canvas editor with composed templates, 50 fonts, icons, shapes, and dynamic recipient fields. Design right in the browser — no Photoshop, and no signup required to try it.')}
            </p>
            {/* Recognition template preview + try-it link */}
            <div className="mt-4 flex items-center gap-4">
              <div className="flex gap-2 overflow-hidden">
                {['blue', 'purple', 'green', 'gold'].map((c) => (
                  <img
                    key={c}
                    src={`/assets/badge-templates/generated/gen-recognition-${c}-preview.png`}
                    alt=""
                    className="h-16 w-16 rounded-lg border border-white/10 bg-white/5 object-contain p-1 opacity-80 group-hover:opacity-100 transition"
                    loading="lazy"
                  />
                ))}
              </div>
              <Link to="/designer" className="whitespace-nowrap text-sm font-medium text-purple-400 hover:text-purple-300 transition">
                {t('features.designer.tryCta', 'Try it free →')}
              </Link>
            </div>
          </div>

          {/* Bulk Award */}
          <div className="group rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-1 hover:border-purple-500/30">
            <div className="mb-4 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold">{t('features.bulk.title', 'Bulk Awards via CSV')}</h3>
            <p className="mt-2 text-sm text-gray-400">
              {t('features.bulk.desc', 'Award thousands of badges in one upload. Pause, resume, retry — never lose progress. Download results and failures as CSV.')}
            </p>
          </div>

          {/* Certificate designer */}
          <div className="group rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-1 hover:border-purple-500/30">
            <div className="mb-4 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.504-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold">{t('features.cert.title', 'PDF Certificates')}</h3>
            <p className="mt-2 text-sm text-gray-400">
              {t('features.cert.desc', 'Design beautiful, printable PDF certificates in the same editor — and issue a verifiable badge alongside. One tool, both formats.')}
            </p>
          </div>

          {/* Permanent hosting */}
          <div className="group rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-1 hover:border-purple-500/30">
            <div className="mb-4 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold">{t('features.ipfs.title', 'Permanent Image Hosting')}</h3>
            <p className="mt-2 text-sm text-gray-400">
              {t('features.ipfs.desc', 'Badge images are hosted permanently on our global CDN — fast, reliable, and always available. No hosting fees, no expiration.')}
            </p>
          </div>

          {/* API-First — span 2 */}
          <div className="group rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-1 hover:border-purple-500/30 sm:col-span-2">
            <div className="mb-4 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold">{t('features.api.title', 'REST API for Developers')}</h3>
            <p className="mt-2 text-sm text-gray-400">
              {t('features.api.desc', 'Full REST API with X-Api-Key auth on every plan. Automate badge creation from your app, LMS, or CI/CD pipeline. No enterprise upgrade required.')}
            </p>
            {/* Code snippet preview */}
            <div className="mt-4 rounded-xl bg-[#0d0d20] border border-white/5 p-4 font-mono text-xs text-gray-400 overflow-hidden">
              <p><span className="text-purple-400">curl</span> -X POST https://api.badges.ninja/badge/award \</p>
              <p className="ml-4">-H <span className="text-green-400">"X-Api-Key: your_key"</span> \</p>
              <p className="ml-4">-d <span className="text-green-400">'{`{"badge":"...", "recipient":"..."}`}'</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* ── USE CASES GRID ── */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <p className="text-sm font-semibold uppercase tracking-widest text-purple-400">
          {t('useCases.label', 'Use Cases')}
        </p>
        <h2 className="mt-2 text-3xl font-bold sm:text-4xl">
          {t('useCases.heading', 'badges.ninja for every team')}
        </h2>
        <p className="mt-4 max-w-xl text-gray-400">
          {t('useCases.subtitle', 'From classrooms to boardrooms — digital badges that fit your use case.')}
        </p>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            {
              title: t('useCases.training.title', 'Training Providers'),
              desc: t('useCases.training.desc', 'Certify course completions with verifiable credentials'),
              icon: (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
                </svg>
              ),
            },
            {
              title: t('useCases.education.title', 'Educational Institutions'),
              desc: t('useCases.education.desc', 'Issue academic badges that employers recognize'),
              icon: (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
              ),
            },
            {
              title: t('useCases.corporate.title', 'Corporate HR'),
              desc: t('useCases.corporate.desc', 'Recognize employee skills and training achievements'),
              icon: (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
              ),
            },
            {
              title: t('useCases.developer.title', 'Developer Communities'),
              desc: t('useCases.developer.desc', 'Award contributors and hackathon winners'),
              icon: (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z" />
                </svg>
              ),
            },
            {
              title: t('useCases.events.title', 'Conferences & Events'),
              desc: t('useCases.events.desc', 'Proof of attendance and workshop completion'),
              icon: (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
              ),
            },
            {
              title: t('useCases.nonprofits.title', 'Nonprofits & Associations'),
              desc: t('useCases.nonprofits.desc', 'Credential volunteers and certified professionals'),
              icon: (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                </svg>
              ),
            },
          ].map((item) => (
            <div key={item.title} className="group rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-1 hover:border-purple-500/30">
              <div className="mb-4 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── TEMPLATE GALLERY ── */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <p className="text-sm font-semibold uppercase tracking-widest text-purple-400">
          {t('templates.label', 'Templates')}
        </p>
        <h2 className="mt-2 text-3xl font-bold sm:text-4xl">
          {t('templates.heading', 'Start from a professional template')}
        </h2>
        <p className="mt-4 max-w-xl text-gray-400">
          {t('templates.subtitle2', 'Polished, ready-to-customise designs. Click one to open it in the free designer — no account needed.')}
        </p>

        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {['gold', 'silver', 'bronze', 'blue', 'green', 'crimson', 'purple', 'graphite'].map((c) => (
            <Link
              key={c}
              to={`/designer?template=gen-recognition-${c}`}
              className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/5 transition hover:-translate-y-1 hover:border-purple-500/40"
            >
              <img
                src={`/assets/badge-templates/generated/gen-recognition-${c}-preview.png`}
                alt={t('templates.recognitionAlt', 'Recognition badge template')}
                className="aspect-square w-full object-contain p-3"
                loading="lazy"
              />
              <span className="absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-[#0a0a1a] via-[#0a0a1a]/70 to-transparent px-2 pb-2 pt-6 text-xs font-medium text-white opacity-0 transition group-hover:opacity-100">
                {t('templates.editThis', 'Edit this template →')}
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
          <Link to="/badge-templates" className="rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-semibold hover:bg-purple-500 transition">
            {t('templates.browseAll', 'Browse all templates')}
          </Link>
          <Link to="/designer" className="text-sm font-medium text-purple-400 hover:text-purple-300 transition">
            {t('templates.designOwn', 'Design your own →')}
          </Link>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <p className="text-center text-sm font-semibold uppercase tracking-widest text-purple-400">
          {t('howItWorks.label', 'Get started in minutes')}
        </p>
        <h2 className="mt-2 text-center text-3xl font-bold sm:text-4xl">
          {t('howItWorks.heading', 'How it works')}
        </h2>

        <div className="mt-14 grid gap-8 sm:grid-cols-3">
          {[
            { step: '1', title: t('howItWorks.step1.title', 'Create an Issuer'), desc: t('howItWorks.step1.desc', 'Register your organization as a badge issuer. If you use your account email, verification is instant.') },
            { step: '2', title: t('howItWorks.step2.title', 'Design a Badge'), desc: t('howItWorks.step2.desc', 'Open the visual editor, pick a template, add your text and branding. Your badge is ready in minutes.') },
            { step: '3', title: t('howItWorks.step3.title', 'Award & Share'), desc: t('howItWorks.step3.desc', 'Issue badges individually or in bulk via CSV. Recipients get a verification URL and can share on LinkedIn, Twitter, and more.') },
          ].map((item) => (
            <div key={item.step} className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-purple-600 text-lg font-bold shadow-lg shadow-purple-600/30">
                {item.step}
              </div>
              <h3 className="mt-4 text-lg font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>


      {/* ── PRICING ── */}
      <section id="pricing" className="scroll-mt-20 mx-auto max-w-6xl px-6 py-24">
        <h2 className="text-center text-3xl font-bold sm:text-4xl">{t('pricing.heading', 'Simple, transparent pricing')}</h2>
        <p className="mx-auto mt-4 max-w-xl text-center text-gray-400">
          {t('pricing.subtitle', 'Start free and scale as you grow. No setup fees, no hidden costs.')}
        </p>
        {/* No setup fee badge */}
        <div className="mt-6 flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-4 py-1.5 text-sm text-green-400">
            <CheckIcon />
            {t('pricing.noSetupFee', 'No setup fee on any plan')}
          </span>
        </div>
        <p className="mt-3 text-center text-xs text-gray-500">
          {t('pricing.noCard', 'No credit card required to start · Cancel anytime')}
        </p>
        <p className="mt-4 text-center text-gray-400">
          {t('pricing.credlyLink', 'Curious how much Credly costs by comparison?')}{' '}
          <Link to="/blog/credly-pricing-for-issuers" className="text-purple-400 underline hover:text-purple-300">
            {t('pricing.credlyLinkCta', 'See our Credly pricing breakdown for issuers')}
          </Link>
        </p>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {/* Free */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8">
            <h3 className="text-lg font-semibold">{t('pricing.free.name', 'Free')}</h3>
            <p className="mt-4"><span className="text-4xl font-bold">$0</span><span className="text-gray-400"> / {t('pricing.month', 'mo')}</span></p>
            <Link to="/signup" className="mt-6 block rounded-lg border border-white/10 py-2.5 text-center text-sm font-medium hover:bg-white/5 transition">
              {t('pricing.free.cta', 'Get Started')}
            </Link>
            <ul className="mt-8 space-y-3 text-sm text-gray-300">
              {['2 issuers', '5 badges', '1,200 credentials / yr', '2 seats', '1 API key', 'Community support'].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckIcon />{f}</li>
              ))}
            </ul>
          </div>

          {/* Starter — highlighted */}
          <div className="relative rounded-2xl border-2 border-purple-500 bg-white/5 p-8 shadow-lg shadow-purple-500/10">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-purple-600 px-3 py-0.5 text-xs font-semibold uppercase tracking-wider">
              {t('pricing.popular', 'Most Popular')}
            </span>
            <h3 className="text-lg font-semibold">{t('pricing.starter.name', 'Starter')}</h3>
            <p className="mt-4"><span className="text-4xl font-bold">$9</span><span className="text-gray-400"> / {t('pricing.month', 'mo')}</span></p>
            <Link to="/signup" className="mt-6 block rounded-lg bg-purple-600 py-2.5 text-center text-sm font-semibold hover:bg-purple-500 transition">
              {t('pricing.starter.cta', 'Get Started')}
            </Link>
            <ul className="mt-8 space-y-3 text-sm text-gray-300">
              {['5 issuers', '25 badges', '12,000 credentials / yr', '5 seats', '5 API keys', 'Email support'].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckIcon />{f}</li>
              ))}
            </ul>
          </div>

          {/* Pro */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8">
            <h3 className="text-lg font-semibold">{t('pricing.pro.name', 'Pro')}</h3>
            <p className="mt-4"><span className="text-4xl font-bold">$29</span><span className="text-gray-400"> / {t('pricing.month', 'mo')}</span></p>
            <Link to="/signup" className="mt-6 block rounded-lg border border-white/10 py-2.5 text-center text-sm font-medium hover:bg-white/5 transition">
              {t('pricing.pro.cta', 'Get Started')}
            </Link>
            <ul className="mt-8 space-y-3 text-sm text-gray-300">
              {['10 issuers', '50 badges', '120,000 credentials / yr', '20 seats', '20 API keys', 'Priority support'].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckIcon />{f}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Every plan includes the whole product — plans differ only by volume */}
        <div className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
          <p className="text-sm font-semibold text-gray-200">
            {t('pricing.everyPlanHeading', 'Every plan includes the whole product')}
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-400">
            {['Visual badge designer', 'Bulk CSV import', 'Analytics dashboard', 'Full REST API', 'Permanent image hosting', 'Open Badges 2.0 & 3.0', 'Public verification pages'].map((f) => (
              <span key={f} className="flex items-center gap-1.5"><CheckIcon />{f}</span>
            ))}
          </div>
        </div>

        {/* Save vs Certifier callout */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            {t('pricing.saveCallout', 'Save up to 92% vs Certifier. Our Starter plan is $9/mo — theirs starts at $119/mo.')}
          </p>
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="mx-auto max-w-4xl px-6 py-24">
        <h2 className="text-center text-3xl font-bold sm:text-4xl">
          {t('comparison.heading', 'More features,')} <span className="text-purple-400">{t('comparison.headingHighlight', '92% less cost')}</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-center text-gray-400">
          {t('comparison.subtitle', 'See how badges.ninja compares to Credly and Certifier — the same features at a fraction of the price.')}
        </p>

        <div className="mt-12 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="py-3 pr-4 font-medium text-gray-400">{t('comparison.feature', 'Feature')}</th>
                <th className="py-3 px-4 font-semibold text-purple-400">badges.ninja</th>
                <th className="py-3 px-4 font-medium text-gray-400">Credly</th>
                <th className="py-3 px-4 font-medium text-gray-400">Certifier</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {[
                { feature: 'Free tier',           us: true,   credly: false,    certifier: true },
                { feature: 'Visual designer',     us: true,   credly: false,    certifier: true },
                { feature: 'Canvas badge editor',  us: true,   credly: false,    certifier: false },
                { feature: 'PDF certificate designer', us: true, credly: false, certifier: true },
                { feature: 'Permanent hosting',         us: true,   credly: false,    certifier: false },
                { feature: 'API access',           us: true,   credly: true,     certifier: true },
                { feature: 'Bulk issuing',         us: true,   credly: true,     certifier: true },
                { feature: 'Setup fee',            us: false,  credly: true,     certifier: false },
                { feature: 'Setup fee amount',     us: '$0',   credly: 'Custom', certifier: '$0' },
                { feature: 'Starter price',        us: '$9/mo', credly: 'Custom', certifier: '$119/mo' },
              ].map((row) => (
                <tr key={row.feature}>
                  <td className="py-3 pr-4 text-gray-300">{row.feature}</td>
                  <td className="py-3 px-4 bg-purple-500/5">
                    {typeof row.us === 'boolean' ? (row.us ? <CheckIcon /> : <XIcon />) : <span className="font-semibold text-purple-400">{row.us}</span>}
                  </td>
                  <td className="py-3 px-4">
                    {typeof row.credly === 'boolean' ? (row.credly ? <CheckIcon /> : <XIcon />) : <span className="text-gray-400">{row.credly}</span>}
                  </td>
                  <td className="py-3 px-4">
                    {typeof row.certifier === 'boolean' ? (row.certifier ? <CheckIcon /> : <XIcon />) : <span className="text-gray-400">{row.certifier}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── WEDGE: beautiful, verifiable, yours ── */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <p className="text-center text-sm font-semibold uppercase tracking-widest text-purple-400">
          {t('wedge.label', 'Why badges.ninja')}
        </p>
        <h2 className="mt-2 text-center text-3xl font-bold sm:text-4xl">
          {t('wedge.heading', 'Beautiful. Verifiable. Yours.')}
        </h2>

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {/* Beautiful */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="mb-3 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold">{t('wedge.beautiful.title', 'Beautiful')}</h3>
            <p className="mt-2 text-sm text-gray-400">{t('wedge.beautiful.desc', 'Design badges and certificates that look like your brand — not a template. Two artifacts from one visual editor.')}</p>
            <Link to="/designer" className="mt-4 inline-block text-sm font-medium text-purple-400 hover:text-purple-300 transition">{t('wedge.beautiful.cta', 'Open the designer →')}</Link>
          </div>
          {/* Verifiable */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="mb-3 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold">{t('wedge.verifiable.title', 'Verifiable')}</h3>
            <p className="mt-2 text-sm text-gray-400">{t('wedge.verifiable.desc', 'Every credential is independently verifiable, forever — Open Badges 2.0 & 3.0 (W3C Verifiable Credentials), permanently hosted. See for yourself below.')}</p>
            <a href="#verify" className="mt-4 inline-block text-sm font-medium text-purple-400 hover:text-purple-300 transition">{t('wedge.verifiable.cta', 'Verify a credential →')}</a>
          </div>
          {/* Yours */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="mb-3 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold">{t('wedge.yours.title', 'Yours')}</h3>
            <p className="mt-2 text-sm text-gray-400">{t('wedge.yours.desc', 'Your issuer identity and your brand, on portable Open Badges 2.0 & 3.0 credentials backed by a public verification page. No lock-in.')}</p>
            <a href={localizedDocsHref('/docs/overview', i18n.language)} className="mt-4 inline-block text-sm font-medium text-purple-400 hover:text-purple-300 transition">{t('wedge.yours.cta', 'Learn more →')}</a>
          </div>
        </div>

        {/* Live verification demo */}
        <div id="verify" className="mt-8 scroll-mt-20">
          <VerifyDemo />
        </div>
      </section>

      {/* ── RESOURCES ── */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <p className="text-sm font-semibold uppercase tracking-widest text-purple-400">
          {t('resources.label', 'Resources')}
        </p>
        <h2 className="mt-2 text-3xl font-bold sm:text-4xl">
          {t('resources.heading', 'Learn more about digital badges')}
        </h2>

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {[
            {
              title: t('resources.overview.title', 'What are Digital Badges?'),
              desc: t('resources.overview.desc', 'Learn the fundamentals of verifiable digital credentials and the Open Badge standard.'),
              href: '/docs/overview',
              icon: (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
              ),
            },
            {
              title: t('resources.guide.title', 'Getting Started Guide'),
              desc: t('resources.guide.desc', 'Create your first issuer, design a badge, and award it in under 5 minutes.'),
              href: '/docs/getting-started',
              icon: (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                </svg>
              ),
            },
            {
              title: t('resources.api.title', 'API Quick Start'),
              desc: t('resources.api.desc', 'Authenticate, create badges, and issue awards programmatically with our REST API.'),
              href: '/docs/api/authentication',
              icon: (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                </svg>
              ),
            },
          ].map((item) => (
            <a
              key={item.title}
              href={item.href}
              className="group rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-1 hover:border-purple-500/30"
            >
              <div className="mb-4 inline-flex rounded-xl bg-purple-500/10 p-3 text-purple-400">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold group-hover:text-purple-400 transition">{item.title}</h3>
              <p className="mt-2 text-sm text-gray-400">{item.desc}</p>
            </a>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="mx-auto max-w-3xl px-6 py-24">
        <h2 className="text-center text-3xl font-bold sm:text-4xl">{t('faq.heading', 'Frequently asked questions')}</h2>

        <div className="mt-12 space-y-4">
          {[
            {
              q: t('faq.q1', 'What is a digital badge?'),
              a: t('faq.a1', 'A digital badge is a visual, verifiable representation of a skill, achievement, or credential. Unlike paper certificates, digital badges contain metadata about the issuer, criteria, and evidence, making them easy to verify and share online.'),
            },
            {
              q: t('faq.q2', 'Is badges.ninja Open Badge compliant?'),
              a: t('faq.a2', 'Yes. Every credential is a hosted Open Badges 2.0 assertion by default, and can also be exported as an Open Badges 3.0 / W3C Verifiable Credential — signed with our did:web issuer key and ready to import into verifiable-credential wallets. That keeps your badges interoperable with any platform that supports the standards, including LinkedIn and Credly.'),
            },
            {
              q: t('faq.q3', 'How are badge images stored?'),
              a: t('faq.a3', 'Badge images are hosted on our global CDN and served from a permanent, immutable URL — fast to load and always available.'),
            },
            {
              q: t('faq.q4', 'Can I use badges.ninja for free?'),
              a: t('faq.a4', 'Absolutely. The free plan includes 2 issuers, 5 badge classes, 1,200 credentials per year, and full access to the badge designer and every feature. No credit card required.'),
            },
            {
              q: t('faq.q5', 'How does badges.ninja compare to Credly?'),
              a: t('faq.a5', 'Credly is enterprise-focused with custom pricing and no self-service sign-up. badges.ninja offers a free tier, a visual badge designer (which Credly lacks), and permanent image hosting — all starting at $0/mo. You can be issuing badges in minutes, not weeks.'),
            },
            {
              q: t('faq.q6', 'Can I migrate from another platform?'),
              a: t('faq.a6', 'Yes. If you have existing badges on another platform, you can recreate your badge designs using our visual editor or upload your own badge images. Our CSV bulk import makes it easy to re-issue credentials to all your existing recipients. Contact support if you need help with migration.'),
            },
          ].map((item) => (
            <details key={item.q} className="group rounded-2xl border border-white/10 bg-white/5">
              <summary className="flex cursor-pointer items-center justify-between px-6 py-4 text-base font-medium [&::-webkit-details-marker]:hidden">
                {item.q}
                <svg className="h-5 w-5 shrink-0 text-gray-400 transition group-open:rotate-45" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </summary>
              <p className="px-6 pb-4 text-sm text-gray-400">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="relative overflow-hidden py-24">
        <div className="absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-1/2 h-80 w-[40rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-600/20 blur-[128px]" />
        </div>
        <div className="mx-auto max-w-2xl px-6 text-center">
          <h2 className="text-3xl font-bold sm:text-4xl">{t('cta.heading', 'Ready to start issuing badges?')}</h2>
          <p className="mt-4 text-gray-400">{t('cta.subtitle2', 'Design a badge free and issue your first verifiable credential in 5 minutes — no credit card.')}</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/designer"
              className="inline-block rounded-lg bg-purple-600 px-8 py-3 text-base font-semibold hover:bg-purple-500 transition shadow-lg shadow-purple-600/25"
            >
              {t('cta.designButton', 'Design a badge free')}
            </Link>
            <Link
              to="/signup"
              className="inline-block rounded-lg border border-white/10 px-8 py-3 text-base font-medium text-gray-300 hover:bg-white/5 transition"
            >
              {t('cta.signupButton', 'Sign up free')}
            </Link>
          </div>
          {/* Trust badges */}
          <p className="mt-6 text-sm text-gray-500">
            Open Badges 2.0 &amp; 3.0 &bull; Permanently Hosted &bull; No Setup Fees &bull; No Credit Card
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-white/5">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="grid gap-10 sm:grid-cols-4">
            {/* Brand */}
            <div>
              <img src="/assets/logo/logo-full-white-text.png" alt="badges.ninja" className="h-7" />
              <p className="mt-3 text-sm text-gray-500">{t('footer.tagline', 'Professional digital credentials')}</p>
            </div>
            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-400">{t('footer.product', 'Product')}</h4>
              <ul className="mt-3 space-y-2 text-sm text-gray-500">
                <li><a href="#pricing" className="hover:text-white transition">{t('footer.pricing', 'Pricing')}</a></li>
                <li><a href={localizedDocsHref('/docs/guide/auth', i18n.language)} className="hover:text-white transition">{t('footer.docs', 'Documentation')}</a></li>
                <li><a href={localizedDocsHref('/docs/api/', i18n.language)} className="hover:text-white transition">{t('footer.apiRef', 'API Reference')}</a></li>
                <li><a href={localizedBlogHref('/blog/', i18n.language)} className="hover:text-white transition">{t('footer.blog', 'Blog')}</a></li>
                <li><a href={localizedBlogHref('/blog/credly-alternative', i18n.language)} className="hover:text-white transition">{t('footer.featuredArticle', 'Credly alternative')}</a></li>
              </ul>
            </div>
            {/* Resources */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-400">{t('footer.resources', 'Resources')}</h4>
              <ul className="mt-3 space-y-2 text-sm text-gray-500">
                <li><a href={localizedDocsHref('/docs/overview', i18n.language)} className="hover:text-white transition">{t('footer.whatAreBadges', 'What are Digital Badges?')}</a></li>
                <li><a href={localizedDocsHref('/docs/getting-started', i18n.language)} className="hover:text-white transition">{t('footer.gettingStarted', 'Getting Started')}</a></li>
                <li><a href={localizedDocsHref('/docs/api/authentication', i18n.language)} className="hover:text-white transition">{t('footer.apiQuickStart', 'API Quick Start')}</a></li>
              </ul>
            </div>
            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-400">{t('footer.legal', 'Legal')}</h4>
              <ul className="mt-3 space-y-2 text-sm text-gray-500">
                <li><Link to="/terms-of-service" className="hover:text-white transition">{t('footer.terms', 'Terms of Service')}</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition">{t('footer.privacy', 'Privacy Policy')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 border-t border-white/5 pt-6 text-center text-xs text-gray-600">
            <Trans i18nKey="footer.powered" t={t}>
              Powered by <a href="https://www.bws.ninja" target="_blank" rel="noopener noreferrer" className="no-underline hover:text-white transition-colors">BWS</a>
            </Trans>
          </div>
        </div>
      </footer>
    </div>
  );
}
